import chain from '@/lib/utils/chain';
import {
  EDITOR_CANVAS_WIDTH,
  EDITOR_CANVAS_GRID_MARGIN,
  EDITOR_CANVAS_GRID_COL_NUM,
  EDITOR_CANVAS_GRID_ROW_HEIGHT,
} from '@/lib/constants';

export function bottomYPosition(modules) {
  return chain(modules)
    .map((mod) => mod.layout.y_position + mod.layout.height)
    .concat([0])
    .max()
    .value();
}

/**
 * Assigns unique z_positions to the provided modules in order.
 * @param {Array} modules Modules to assign z_positions to.
 */
export function assignUniqueZPositions(modules) {
  return modules.map((mod, index) => ({
    ...mod,
    layout: {
      ...mod.layout,
      z_position: index + 1,
    },
  }));
}

/**
 * Converts width in grid columns to pixels including margins in between.
 * @param {Number} value Width in grid columns
 */
export function widthToPixels(value) {
  const totalMarginsWidth = (EDITOR_CANVAS_GRID_COL_NUM + 1) * EDITOR_CANVAS_GRID_MARGIN;
  const singleColumnWidth = (EDITOR_CANVAS_WIDTH - totalMarginsWidth) / EDITOR_CANVAS_GRID_COL_NUM;
  const marginBetweenColumns = (value - 1) * EDITOR_CANVAS_GRID_MARGIN;
  return marginBetweenColumns + singleColumnWidth * value;
}

/**
 * Converts height in grid rows to pixels including margins in between.
 * @param {Number} value Height in rows.
 */
export function heightToPixels(value) {
  const marginBetweenRows = (value - 1) * EDITOR_CANVAS_GRID_MARGIN;
  return EDITOR_CANVAS_GRID_ROW_HEIGHT * value + marginBetweenRows;
}

/**
 * Converts x_position in grid columns to pixels.
 * @param {Number} value x_position in grid columns
 */
export function xPositionToPixels(value) {
  const totalMarginsWidth = (EDITOR_CANVAS_GRID_COL_NUM + 1) * EDITOR_CANVAS_GRID_MARGIN;
  const singleColumnWidth = (EDITOR_CANVAS_WIDTH - totalMarginsWidth) / EDITOR_CANVAS_GRID_COL_NUM;
  const marginPixels = (value + 1) * EDITOR_CANVAS_GRID_MARGIN;
  return marginPixels + value * singleColumnWidth;
}

/**
 * Converts y_position in grid rows to pixels.
 * @param {Number} value y_position in grid rows
 */
export function yPositionToPixels(value) {
  const marginPixels = (value + 1) * EDITOR_CANVAS_GRID_MARGIN;
  return EDITOR_CANVAS_GRID_ROW_HEIGHT * value + marginPixels;
}

/**
 * Converts module layout from rows/columns to pixels.
 * @param {Object} layout - module layout
 */
export function layoutToPixels(layout, zPosition) {
  return {
    ...layout,
    height: heightToPixels(layout.height),
    width: widthToPixels(layout.width),
    x_position: xPositionToPixels(layout.x_position),
    y_position: yPositionToPixels(layout.y_position),
    z_position: zPosition || 0.5,
  };
}

// Not every grid layout field -- exactly the ones layoutToPixels() converts from
// grid units. z_position and rotate_degree are carried over untouched, so they
// are irrelevant to convertibility and must stay out of this list.
const PAGE_MIGRATION_GRID_LAYOUT_FIELDS = ['height', 'width', 'x_position', 'y_position'];

/**
 * Whether a module carries a layout every converted field of which is numeric.
 *
 * The presence of `layout` is not enough: layoutToPixels() maps a missing or
 * non-numeric field to NaN, which serialises to null and would persist a broken
 * layout under grid_disabled -- unconvertible, because the flag blocks a re-run.
 *
 * @param {Object} mod A page module, as plain JSON.
 * @returns {Boolean} true only when every converted field is a finite number.
 */
export function hasConvertibleGridLayout(mod) {
  if (!mod?.layout) return false;
  return PAGE_MIGRATION_GRID_LAYOUT_FIELDS.every((field) => Number.isFinite(mod.layout[field]));
}

/**
 * Builds the migration converting one legacy grid page to absolute pixels.
 * Pure -- the caller persists patchOps, then applies the returned values.
 *
 * layoutToPixels() is not idempotent, so grid_disabled is the only thing
 * preventing a re-run and a page carrying it is skipped outright.
 *
 * @param {Object} page A draft page, as plain JSON.
 * @returns {Object|null} null when no conversion is needed, otherwise
 *   { patchOps, migratedOptions, migratedLayouts: [{ index, id, layout }] }.
 */
export function buildPageGridMigration(page) {
  if (!page || page.options?.grid_disabled) return null;

  const modules = page.modules || [];
  // A module with no convertible layout has no grid coordinates to map, so it
  // is left alone rather than converted into NaN.
  const migratedLayouts = modules
    .map((mod, index) => ({ index, mod }))
    .filter(({ mod }) => hasConvertibleGridLayout(mod))
    .map(({ index, mod }) => ({
      index,
      id: mod.id,
      layout: layoutToPixels(mod.layout),
    }));

  const migratedOptions = {
    ...(page.options || {}),
    grid_disabled: true,
  };

  // 'add' not 'replace': the backend reports a never-set field as absent, which
  // makes 'replace' raise. module_id lets it reject a stale index after a reorder.
  const patchOps = migratedLayouts.map(({ index, id, layout }) => ({
    op: 'add',
    path: `/pages/${page.id}/modules/${index}/layout`,
    value: layout,
    module_id: id,
  }));

  // Unconditional, even with no layouts to convert: the flag is what marks the
  // page as no longer on the grid, and it is the only thing stopping a re-run.
  //
  // The whole options object, not /options/grid_disabled: a page that has never
  // carried options cannot be addressed at the nested path.
  patchOps.push({
    op: 'add',
    path: `/pages/${page.id}/options`,
    value: migratedOptions,
  });

  return { patchOps, migratedOptions, migratedLayouts };
}

/**
 * Converts a height in pixels to grid rows.
 * @param {Number} heightInPixels Height in pixels
 */
export function pixelsToHeight(heightInPixels) {
  const rowHeight = EDITOR_CANVAS_GRID_ROW_HEIGHT + EDITOR_CANVAS_GRID_MARGIN;
  let rowCount = Math.floor(heightInPixels / rowHeight);
  if (heightInPixels % rowHeight === 0) rowCount -= 1;
  const rowsHeightPixels = rowCount * rowHeight + EDITOR_CANVAS_GRID_ROW_HEIGHT;
  rowCount = heightInPixels <= rowsHeightPixels ? rowCount + 1 : rowCount + 2;
  return rowCount;
}

/**
 * Reformats the layout data stored in Mongo into the format
 * expected by the grid library
 * @param {Object} mod - Module data from the store
 */
export function mapModuleToGridItem(mod) {
  // if missing, apply default values
  return {
    id: mod.id,
    x: mod.layout.x_position || 0,
    y: mod.layout.y_position || 0,
    z: mod.layout.z_position || 0,
    height: mod.layout.height || 350,
    width: mod.layout.width || 350,
    rotation: mod.layout.rotate_degree || 0,
  };
}

/**
 * Gets the bounding box for the given array of points.
 */
export function getBoundingBox(points) {
  if (!points?.length) {
    return {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      width: 0,
      height: 0,
    };
  }
  const xValues = points.map((point) => point.x);
  const yValues = points.map((point) => point.y);
  const left = Math.min(...xValues);
  const right = Math.max(...xValues);
  const top = Math.min(...yValues);
  const bottom = Math.max(...yValues);

  return {
    left,
    right,
    top,
    bottom,
    width: right - left,
    height: bottom - top,
  };
}

/**
 * Returns true if the two bounding boxes overlap each other.
 */
export function boundingBoxesOverlap(boxOne, boxTwo) {
  const xAxisOverlap = (boxOne.left >= boxTwo.left && boxOne.left <= boxTwo.right)
    || (boxOne.right >= boxTwo.left && boxOne.right <= boxTwo.right)
    || (boxTwo.left >= boxOne.left && boxTwo.left <= boxOne.right)
    || (boxTwo.right >= boxOne.left && boxTwo.right <= boxOne.right);
  const yAxisOverlap = (boxOne.top >= boxTwo.top && boxOne.top <= boxTwo.bottom)
    || (boxOne.bottom >= boxTwo.top && boxOne.bottom <= boxTwo.bottom)
    || (boxTwo.top >= boxOne.top && boxTwo.top <= boxOne.bottom)
    || (boxTwo.bottom >= boxOne.top && boxTwo.bottom <= boxOne.bottom);
  return xAxisOverlap && yAxisOverlap;
}

export default {
  bottomYPosition,
  assignUniqueZPositions,
  widthToPixels,
  heightToPixels,
  xPositionToPixels,
  yPositionToPixels,
  layoutToPixels,
  hasConvertibleGridLayout,
  buildPageGridMigration,
  pixelsToHeight,
  getBoundingBox,
  boundingBoxesOverlap,
};
